import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi, RCCTooltip } from "react-common-components";
import AccordionList from "src/components/AccordionList";
import Loader from "src/components/Loader";
import SortableHandle from "src/components/Sortable/Handle";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { simplePermissionType } from "src/components/Permissions/interface";
import { CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER } from "src/components/Accordion";
import { IMatba001Form, IMatba003, IMatba004 } from "../interface";
import { IRefModalLevel2 } from "./ModalLevel2";
import ListOfLevel3, { IRefLevel3 } from "./listOfLevel3";

export interface IListOfLevel2{
    analysisData: IMatba001Form;
    level2Data: IMatba003;
    refModalLevel2: React.RefObject<IRefModalLevel2>; 
    onCallbackEvaluation: () => void;
	onCallbackChangeLevel2: (level2: IMatba003) => void;
	onCallbackDeleteLevel2: (id: number) => void;
  }

const ListOfLevel2: React.FC<IListOfLevel2> = ({
	analysisData,
	level2Data,
	refModalLevel2,
	onCallbackEvaluation,
	onCallbackChangeLevel2,
	onCallbackDeleteLevel2
}) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const [loading, setLoading] = useState(false);
	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [ data, setData ] = useState<IMatba004[] | null>(null);

	const refLevel3 = useRef<IRefLevel3>(null);
  	
	const loadData = async (addItem?: boolean) => {
		try{
			setLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "Analysis/ListLevel3",
				urlParameters: {
					id: level2Data.maa003Id
				}
			});

			setData(response.data);
			setLoading(false);

			if(addItem){
				refLevel3.current?.addItem(level2Data.maa003Id);
			}

		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setLoading(false);
		}
	};

	const handleAccordion = (expanded:boolean, addItem?: boolean) => {
		setExpandedAccordion(expanded);
		if(!expanded)
			return;
		loadData(addItem);
	};

	const addItemLevel3 = (row: any) => {
		if(data === null){
			handleAccordion(true, true);
		}else{
			refLevel3.current?.addItem(row.maa003Id);
		}		
	};

	const options = (row: any) => (
		<>
			{
				analysisData.userPermission > Number(simplePermissionType.VIEW) &&
				<RCCGrid
					container 
					item
					justifyContent="flex-end"
					xs={12}
					className={CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Adicionar {{label}}", {label: analysisData.maa001Label3})}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", size: 12}}
								outlined
								color="purple"
								size="xs"
								layout="isRounded"
								onClick={(e) => {
									e.stopPropagation(); 
									addItemLevel3(row);
								}}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Editar {{label}}", {label: analysisData.maa001Label2})}
							color="grey"
						>
							<RCCButton
								icon={{name: "pencil", size: 12}}
								outlined
								color="grey"
								size="xs"
								layout="isRounded"
								onClick={(e) => {e.stopPropagation(); refModalLevel2.current?.actionButtonChange(row.maa003Id, analysisData.maa001Label2, onCallbackChangeLevel2);}}
							/>
						</RCCTooltip>
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCTooltip
							text={translate("Excluir {{label}}", {label: analysisData.maa001Label2})}
							color="red"
						>
							<RCCButton
								icon={{name: "trash", size: 12}}
								outlined
								color="red"
								size="xs"
								layout="isRounded"
								onClick={(e) => {e.stopPropagation(); refModalLevel2.current?.actionButtonDelete(row.maa003Id, row.maa003Descricao, onCallbackDeleteLevel2);}}
							/>
						</RCCTooltip>
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<SortableHandle>
							<RCCTooltip 
								color="grey"
								text={translate("Mover {{label}}", {label: analysisData.maa001Label2})}
							>
								<RCCButton
									color="grey"
									outlined
									icon={{
										name: "arrows",
										color: "var(--grey)"
									}}
									layout="isRounded"
									onClick={(e) => e.stopPropagation()}
								/>
							</RCCTooltip>	
						</SortableHandle>
					</CheckPermission>
				</RCCGrid>
			}
		</>
	);

	return (
		<>
			<AccordionList 
				expanded={expandedAccordion}
				showExpandIcon
				onChange={(e, expanded) => {e.stopPropagation(); handleAccordion(expanded);}}
				header={
					<>
						<RCCGrid 
							container
							item
							justifyContent="space-between"
							alignItems="center"
							xs={12}
						>
							<RCCGrid
								item
								md={9}
								xs={12}
								styleInline="display: flex; color: var(--default-color-font);"
							>
								<span>{level2Data.maa003Descricao}</span>
								{
									level2Data.maa003Explicacao &&
									<RCCPopover
										position="bottom"
										header={{title:translate("Explicação")}}
										body={{text:`${level2Data.maa003Explicacao}`}}
									>
										<RCCIcon
											style={{marginLeft: 5}}
											name="infoCircle"
											color="grey"
										/>
									</RCCPopover>
								}			
							</RCCGrid>
							<RCCGrid
								container 
								item
								justifyContent="flex-end"
								md={3}
								xs={12}
							>
								{options(level2Data)}		
							</RCCGrid>
						</RCCGrid>
					</>
				}
			>
				<>
					{
						loading ? (
							<Loader />
						) : (
							<RCCGrid container>
								<ListOfLevel3
									ref={refLevel3} 
									data={data || []}
									analysisData={analysisData}
									onCallbackEvaluation={onCallbackEvaluation}
								/>		
							</RCCGrid>
						)
					}
				</>
			</AccordionList>
		</>
	);
};

export default ListOfLevel2;
